import { useQueries, useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { get } from "@/lib/api";
import { needsYouSub } from "@/lib/documents";
import type { Approval, DocumentDetail } from "@/lib/types";

/** Open requests from agents, newest first; fresh on every visit. */
export function usePendingApprovals() {
  return useQuery({
    queryKey: ["approvals", "pending"],
    queryFn: () => get<{ approvals: Approval[] }>("/api/approvals?status=pending").then((r) => r.approvals),
    staleTime: 0,
  });
}

/** „3 Dinge warten auf dich“ for the line under the page title. */
export function waitingLine(n: number): string {
  return n === 1 ? "1 Ding wartet auf dich" : `${n} Dinge warten auf dich`;
}

/** „Braucht dich“: one row per pending approval, in the order given (the API lists the newest first); renders nothing while there are none. */
export function NeedsYou({ approvals, error, onRetry }: { approvals: Approval[]; error?: string | null; onRetry?: () => void }) {
  // The sub line needs the total and the checks, which only the document carries.
  const docs = useQueries({
    queries: approvals.map((a) => ({
      queryKey: ["document", a.documentId],
      queryFn: () => get<DocumentDetail>(`/api/documents/${a.documentId}`),
    })),
  });
  if (error) {
    return (
      <section aria-labelledby="needs-you-title" className="space-y-3">
        <h2 id="needs-you-title" className="text-base font-semibold">Braucht dich</h2>
        <p role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
          Freigaben konnten nicht geladen werden
          {onRetry && <Button type="button" variant="outline" size="sm" onClick={onRetry}>Erneut versuchen</Button>}
        </p>
      </section>
    );
  }
  if (approvals.length === 0) return null;
  return (
    <section aria-labelledby="needs-you-title" className="space-y-3">
      <h2 id="needs-you-title" className="text-base font-semibold">Braucht dich</h2>
      <ul className="divide-y rounded-xl border bg-card">
        {approvals.map((a, i) => {
          const detail = docs[i]?.data;
          return (
            <li key={a.id} className="grid grid-cols-[0.5rem_minmax(0,1fr)_auto] items-center gap-x-3.5 gap-y-2 px-4 py-3.5 max-sm:grid-cols-[0.5rem_minmax(0,1fr)]">
              <span aria-hidden className="size-2 rounded-full bg-warning" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-pretty">{a.title}</p>
                <p className="text-[0.8125rem] text-muted-foreground tabular-nums">
                  {detail ? needsYouSub(a, detail.document.totalCents, detail.validation) : " "}
                </p>
              </div>
              <Button asChild size="sm" className="max-sm:col-start-2 max-sm:justify-self-start">
                <Link to={`/rechnungen/${a.documentId}?freigabe=${a.id}`}>Prüfen &amp; freigeben</Link>
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
